/* Phitkoo v3 extras: add-to-cart animation, scroll reveal, back to top, review button, reviews empty state. */
(() => {
  // ---- Add to cart: fly the photo into the cart, then confirm on the button ----
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const pending = new WeakMap();
  const photoFor = form => form.closest('.product-card')?.querySelector('.product-card__media img')
    || (form.closest('[data-product-root]') && document.querySelector('[data-product-main-image]'))
    || document.querySelector('[data-product-main-image]');
  const fly = img => {
    const cart = document.querySelector('[data-cart-link]');
    if (!img || !cart || reduce.matches) return;
    const from = img.getBoundingClientRect();
    const to = cart.getBoundingClientRect();
    if (!from.width || !to.width) return;
    const size = Math.min(from.width, from.height, 140);
    const clone = document.createElement('img');
    clone.src = img.currentSrc || img.src;
    clone.className = 'pk-fly';
    clone.alt = '';
    Object.assign(clone.style, {width: size + 'px', height: size + 'px', left: (from.left + from.width / 2 - size / 2) + 'px', top: (from.top + from.height / 2 - size / 2) + 'px'});
    document.body.append(clone);
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    clone.animate([
      {transform: 'translate(0,0) scale(1)', opacity: 1},
      {transform: `translate(${dx * .5}px, ${dy * .5 - 60}px) scale(.55)`, opacity: 1, offset: .55},
      {transform: `translate(${dx}px, ${dy}px) scale(.12)`, opacity: .2}
    ], {duration: 800, easing: 'cubic-bezier(.55,0,.25,1)'}).onfinish = () => {
      clone.remove();
      cart.classList.remove('pk-bump'); void cart.offsetWidth; cart.classList.add('pk-bump');
    };
  };
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[data-ajax-cart]')) return;
    const button = form.querySelector('button[type="submit"]');
    if (!button || pending.has(button)) return;
    pending.set(button, photoFor(form));
    const addedLabel = document.body.dataset.cartAdded;
    const watch = new MutationObserver(() => {
      if (addedLabel && button.textContent.trim() === addedLabel.trim()) {
        fly(pending.get(button));
        button.classList.add('pk-added');
        setTimeout(() => button.classList.remove('pk-added'), 1300);
        done();
      } else if (!button.hasAttribute('aria-busy') && form.querySelector('[data-cart-error]')?.textContent) done();
    });
    const done = () => { watch.disconnect(); pending.delete(button); };
    watch.observe(button, {childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['aria-busy']});
    setTimeout(done, 15000);
  }, true);

  // ---- Gentle reveal on scroll ----
  const reveal = () => {
    if (reduce.matches || !('IntersectionObserver' in window)) return;
    const targets = document.querySelectorAll('main .section-head, main .product-card, main .mascot-story, main .world-card, main .phitkoo-poster, main .jdgm-review-widget, main .product-details__grid');
    const io = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      el.classList.add('is-in');
      io.unobserve(el);
      // hand control back to the normal hover styles once the entrance is done
      setTimeout(() => { el.classList.remove('pk-reveal', 'is-in'); el.style.removeProperty('--pk-delay'); }, 1400);
    }), {rootMargin: '0px 0px -8% 0px'});
    targets.forEach(el => {
      if (el.getBoundingClientRect().top < innerHeight * .9) return; // already on screen: leave it alone
      const siblings = Array.from(el.parentElement?.children || []);
      el.style.setProperty('--pk-delay', Math.min(siblings.indexOf(el), 4) * 0.08 + 's');
      el.classList.add('pk-reveal');
      io.observe(el);
    });
  };

  // ---- Back to top ----
  const backToTop = () => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pk-top';
    btn.setAttribute('aria-label', 'Back to top');
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
    btn.addEventListener('click', () => scrollTo({top: 0, behavior: reduce.matches ? 'auto' : 'smooth'}));
    document.body.append(btn);
    let t = false;
    addEventListener('scroll', () => { if (t) return; t = true; requestAnimationFrame(() => { t = false; btn.classList.toggle('is-visible', scrollY > innerHeight * 1.5); }); }, {passive: true});
  };


  // ---- Reviews: "Review this product" button + always-visible "What the tribe says" ----
  const waitFor = (selector, timeout = 8000) => new Promise(resolve => {
    const found = document.querySelector(selector);
    if (found) return resolve(found);
    const obs = new MutationObserver(() => { const el = document.querySelector(selector); if (el) { obs.disconnect(); resolve(el); } });
    obs.observe(document.body, {childList: true, subtree: true});
    setTimeout(() => { obs.disconnect(); resolve(document.querySelector(selector)); }, timeout);
  });
  const openReviewForm = async () => {
    const widget = document.querySelector('#judgeme_product_reviews, .jdgm-review-widget') || await waitFor('#judgeme_product_reviews, .jdgm-review-widget');
    if (!widget) return;
    widget.scrollIntoView({behavior: reduce.matches ? 'auto' : 'smooth', block: 'start'});
    const link = await waitFor('.jdgm-write-rev-link');
    const formWrap = document.querySelector('.jdgm-form-wrapper');
    const formOpen = formWrap && formWrap.offsetParent !== null && getComputedStyle(formWrap).display !== 'none';
    if (link && !formOpen) link.click();
  };
  const reviewButton = () => {
    const buyForm = document.querySelector('[data-product-root] .product-main__info form[data-ajax-cart]');
    if (!buyForm || document.querySelector('.pk-review-btn')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button button--outline pk-review-btn';
    btn.innerHTML = '<span aria-hidden="true">&#9733;</span> Review this product';
    btn.addEventListener('click', openReviewForm);
    buyForm.after(btn);
    if (location.hash === '#pk-write-review') addEventListener('load', () => setTimeout(openReviewForm, 600), {once: true});
  };
  const tribeFallback = () => {
    if (!document.body.classList.contains('template-index')) return;
    const block = document.querySelector('[id*="judge_me_reviews_cards_carousel"], .jdgm-cards-carousel, .jdgm-carousel-wrapper');
    const host = block?.closest('.app-section') || document.querySelector('[id$="__1791114492b300dcef"] .app-section');
    if (!host) return;
    const check = () => {
      const hasReviews = Array.from(host.querySelectorAll('.jdgm-carousel-item, .jdgm-carousel__item')).some(el => el.offsetHeight > 0);
      if (hasReviews || host.querySelector('.pk-tribe')) return;
      // Let the shopper pick which Phitkoo they are reviewing.
      const seen = new Set();
      const picks = Array.from(document.querySelectorAll('main .product-card')).map(card => {
        const link = card.querySelector('a[href*="/products/"]');
        const name = (card.querySelector('h3')?.textContent || '').trim().split(' Alum')[0];
        const href = link?.getAttribute('href')?.split('#')[0];
        if (!href || !name || seen.has(href)) return null;
        seen.add(href);
        return '<a class="pk-tribe__pick" href="' + href + '#pk-write-review">' + name.replace(/[<>&"]/g, '') + '</a>';
      }).filter(Boolean);
      const duo = document.querySelector('main a[href*="/products/phitkoo-duo"]');
      if (duo && !seen.has('/products/phitkoo-duo')) picks.push('<a class="pk-tribe__pick" href="/products/phitkoo-duo#pk-write-review">Phitkoo Duo</a>');
      const box = document.createElement('section');
      box.className = 'pk-tribe';
      box.innerHTML = '<div class="eyebrow">REAL TALK</div><h2 class="pk-tribe__title">What the tribe says</h2>' +
        '<div class="pk-tribe__stars" aria-hidden="true">&#9734;&#9734;&#9734;&#9734;&#9734;</div>' +
        '<p>No reviews yet. Tried a Phitkoo? Your review helps the next person pick their rock.</p>' +
        (picks.length
          ? '<p class="pk-tribe__ask">Which one did you try?</p><div class="pk-tribe__picks">' + picks.join('') + '</div>'
          : '<a class="button pk-tribe__cta" href="/collections/all">Write a review</a>');
      host.append(box);
    };
    // Judge.me fills the carousel after load; give it a moment before deciding it is empty.
    addEventListener('load', () => setTimeout(check, 2500), {once: true});
    if (document.readyState === 'complete') setTimeout(check, 2500);
  };

  const boot = () => { reveal(); backToTop(); reviewButton(); tribeFallback(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
